import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  setStage,
  setConfirmation,
  updateItasc,
  setClock,
  toScrub,
  toReal,
  step as stepClock,
  createIncident,
  addUnit,
  updateUnit,
  removeUnit,
  updateWaterPlan,
  setPrimaryType,
  addIncidentType,
  removeIncidentType,
  evaluate,
  createAuditEvent,
  type DutyConfig,
  type Incident,
  type IncidentStage,
  type IncidentType,
  type IncidentLevel,
  type ConfirmationStatus,
  type DeepPartialItasc,
  type ResourceUnit,
  type WaterPlan,
  type Insight,
  type AuditEvent,
  type AuditEventType,
  type LngLat,
  type MemberRoster,
} from '@duty/shared';
import { useResolvedConfig } from './requirements';
import { actorForRole } from '@duty/shared';
import { loadStore, saveStore, type PersistedStore, type PersistedIncidentRecord, type Interaction } from './persistence';
import { remotePutIncident, remoteAppendAudit } from './remote';
import { buildScenarioIncident, advanceToStage, SCENARIO_INCIDENT_ID } from './scenario';
import { spatialInsightContext, datasetFor, waterPlanPatchFromOsmFeatures } from './spatial';
import { fetchOsmWaterFeatures } from './osm-water';
import { useRole } from '@/auth/role';
import bungendoreRosterRaw from '@data/integration/bungendore-crew-roster.json';

type Action =
  | { type: 'load-scenario' }
  | { type: 'new-incident'; name: string; location: string; coordinates?: LngLat; incidentType?: IncidentType; level?: IncidentLevel }
  | { type: 'set-location'; location: string; coordinates?: LngLat }
  | { type: 'clear' }
  | { type: 'set-stage'; stage: IncidentStage }
  | { type: 'set-confirmation'; status: ConfirmationStatus }
  | { type: 'update-itasc'; patch: DeepPartialItasc }
  | { type: 'clock-scrub'; elapsedSec: number }
  | { type: 'clock-step'; deltaSec: number }
  | { type: 'clock-real' }
  | { type: 'add-unit'; unit: ResourceUnit }
  | { type: 'update-unit'; unitId: string; patch: Partial<ResourceUnit> }
  | { type: 'remove-unit'; unitId: string }
  | { type: 'update-water'; patch: Partial<WaterPlan> }
  | { type: 'set-primary-type'; incidentType: IncidentType }
  | { type: 'add-incident-type'; incidentType: IncidentType }
  | { type: 'remove-incident-type'; incidentType: IncidentType };

type RosterAction =
  | { type: 'load-roster'; roster: MemberRoster }
  | { type: 'clear-roster' };

function rosterReducer(state: MemberRoster | null, action: RosterAction): MemberRoster | null {
  switch (action.type) {
    case 'load-roster':
      return action.roster;
    case 'clear-roster':
      return null;
    default:
      return state;
  }
}

/**
 * Per-incident field mutations — the same switch this app has always had.
 * Deliberately takes/returns a real `Incident` (never null): the three
 * actions that used to short-circuit before reaching this point
 * (`load-scenario`/`new-incident`/`clear`) now operate on the outer
 * multi-incident store (creating/selecting a record) before this function
 * is ever called — see `dispatch` in `IncidentProvider`.
 */
function incidentReducer(state: Incident, action: Action): Incident {
  switch (action.type) {
    case 'set-stage':
      return state.id.startsWith('inc_bungendore')
        ? advanceToStage(state, action.stage)
        : setStage(state, action.stage);
    case 'set-confirmation':
      return setConfirmation(state, action.status);
    case 'update-itasc':
      return updateItasc(state, action.patch);
    case 'clock-scrub':
      return setClock(state, toScrub(state.clock, action.elapsedSec));
    case 'clock-step':
      return setClock(state, stepClock(state.clock, action.deltaSec));
    case 'clock-real':
      return setClock(state, toReal(state.clock));
    case 'add-unit':
      return addUnit(state, action.unit);
    case 'update-unit':
      return updateUnit(state, action.unitId, action.patch);
    case 'remove-unit':
      return removeUnit(state, action.unitId);
    case 'set-location':
      return { ...state, location: action.location, ...(action.coordinates ? { coordinates: action.coordinates } : {}) };
    case 'update-water':
      return updateWaterPlan(state, action.patch);
    case 'set-primary-type':
      return setPrimaryType(state, action.incidentType);
    case 'add-incident-type':
      return addIncidentType(state, action.incidentType);
    case 'remove-incident-type':
      return removeIncidentType(state, action.incidentType);
    default:
      return state;
  }
}

interface StoreValue {
  incident: Incident | null;
  dispatch: React.Dispatch<Action>;
  /** Doctrine-resolved config for this incident context (falls back to defaults). */
  config: DutyConfig;
  /** Live insights (candidates with the user's ack/dismiss overlaid). */
  insights: Insight[];
  audit: AuditEvent[];
  /** Named-crew roster from integration feed or manual load (null = standalone). */
  roster: MemberRoster | null;
  loadRoster: (roster: MemberRoster) => void;
  clearRoster: () => void;
  acknowledgeInsight: (id: string) => void;
  dismissInsight: (id: string, reason: string) => void;
  /** Append a manual audit event (export/review/stand-down) as the current role. */
  logEvent: (type: AuditEventType, payload?: Record<string, unknown>) => void;
  /** Live OSM hydrant/water lookup status for the current incident (idle when no coordinates or a seeded locality already applies). */
  osmWaterStatus: 'idle' | 'loading' | 'done' | 'unavailable';
  /** Every open incident, for the portfolio dashboard — order is insertion order, not priority. */
  incidentList: Incident[];
  /** Switch the active incident to one already in `incidentList` (no-op if the id isn't there). */
  switchToIncident: (id: string) => void;
  /** Leave the active incident without closing it — it stays in `incidentList`, reachable via `switchToIncident`. */
  returnToDashboard: () => void;
  /** Permanently remove an incident from the list + local storage. If it was active, also returns to the dashboard. */
  removeIncident: (id: string) => void;
}

const StoreContext = createContext<StoreValue | null>(null);

const bungendoreRoster = bungendoreRosterRaw as unknown as MemberRoster;

export function IncidentProvider({ children }: { children: ReactNode }) {
  const [store, setStore] = useState<PersistedStore>(loadStore);
  const { activeId, recordsById } = store;
  const activeRecord = activeId ? recordsById[activeId] : undefined;
  const incident = activeRecord?.incident ?? null;
  const audit = activeRecord?.audit ?? [];
  const interactions = activeRecord?.interactions ?? {};

  const [roster, dispatchRoster] = useReducer(rosterReducer, null);
  const { role } = useRole();
  const { config, resolvedParams } = useResolvedConfig(incident, role);

  const dispatch = useCallback((action: Action) => {
    setStore((prev) => {
      if (action.type === 'load-scenario') {
        const inc = buildScenarioIncident();
        return {
          recordsById: { ...prev.recordsById, [inc.id]: { incident: inc, audit: [], interactions: {} } },
          activeId: inc.id,
        };
      }
      if (action.type === 'new-incident') {
        const inc = createIncident({
          name: action.name,
          location: action.location,
          coordinates: action.coordinates,
          typeReported: action.incidentType,
          level: action.level,
        });
        return {
          recordsById: { ...prev.recordsById, [inc.id]: { incident: inc, audit: [], interactions: {} } },
          activeId: inc.id,
        };
      }
      if (action.type === 'clear') {
        // Legacy "close incident" semantics — the UI uses returnToDashboard()/
        // removeIncident() instead; this only drops the active pointer, it never
        // deletes the incident's record.
        return { ...prev, activeId: null };
      }
      if (!prev.activeId) return prev;
      const record = prev.recordsById[prev.activeId];
      if (!record) return prev;
      const nextIncident = incidentReducer(record.incident, action);
      if (nextIncident === record.incident) return prev;
      return { ...prev, recordsById: { ...prev.recordsById, [prev.activeId]: { ...record, incident: nextIncident } } };
    });
  }, []);

  const switchToIncident = useCallback((id: string) => {
    setStore((prev) => (prev.recordsById[id] ? { ...prev, activeId: id } : prev));
  }, []);

  const returnToDashboard = useCallback(() => {
    setStore((prev) => ({ ...prev, activeId: null }));
  }, []);

  const removeIncident = useCallback((id: string) => {
    setStore((prev) => {
      if (!(id in prev.recordsById)) return prev;
      const recordsById = { ...prev.recordsById };
      delete recordsById[id];
      return { recordsById, activeId: prev.activeId === id ? null : prev.activeId };
    });
  }, []);

  const incidentList = useMemo(() => Object.values(recordsById).map((r) => r.incident), [recordsById]);

  // Persist the whole store locally (reload-survivable, every open incident) and
  // write-through the active incident to the Table adapter best-effort (debounced).
  // Remote persistence no-ops in standalone mode / while off by default.
  useEffect(() => {
    saveStore(store);
    if (!incident) return;
    const snapshot = incident;
    const handle = window.setTimeout(() => {
      void remotePutIncident(snapshot);
    }, 1000);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  // Flush newly-appended audit events to the Table adapter best-effort, tracked
  // per incident id (not a single counter) so switching between incidents doesn't
  // re-send already-pushed events or lose track of a different incident's progress.
  const pushedAuditRef = useRef<Record<string, number>>({});
  useEffect(() => {
    if (!incident) return;
    const id = incident.id;
    const pushedCount = pushedAuditRef.current[id] ?? 0;
    const pending = audit.slice(pushedCount);
    if (pending.length === 0) return;
    const target = audit.length;
    void remoteAppendAudit(id, pending).then((written) => {
      if (written > 0) pushedAuditRef.current[id] = target;
    });
  }, [audit, incident]);

  // Auto-load the synthetic crew roster for the Bungendore demo scenario;
  // clear it when a different (or no) incident is active.
  useEffect(() => {
    if (incident?.id === SCENARIO_INCIDENT_ID) {
      dispatchRoster({ type: 'load-roster', roster: bungendoreRoster });
    } else {
      dispatchRoster({ type: 'clear-roster' });
    }
  }, [incident?.id]);

  // Derive the Water Plan (nearest hydrant / static water) from a live OSM
  // lookup for any incident whose location isn't the seeded Bungendore demo
  // (that path is derived from the synthetic fixture in scenario.ts instead,
  // to keep the golden regression scenario stable). Never echoes input —
  // computed from real map data, or left "unknown" if OSM is unreachable.
  const [osmWaterStatus, setOsmWaterStatus] = useState<'idle' | 'loading' | 'done' | 'unavailable'>('idle');
  const osmFetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!incident || !incident.coordinates || datasetFor(incident)) {
      setOsmWaterStatus('idle');
      return;
    }
    const point = incident.coordinates;
    const key = `${incident.id}:${point.lng}:${point.lat}`;
    if (osmFetchKeyRef.current === key) return; // already fetched for this incident + point
    osmFetchKeyRef.current = key;
    setOsmWaterStatus('loading');
    let cancelled = false;
    void fetchOsmWaterFeatures(point).then((result) => {
      if (cancelled || osmFetchKeyRef.current !== key) return; // superseded by a newer location
      if (!result) {
        setOsmWaterStatus('unavailable');
        return;
      }
      dispatch({ type: 'update-water', patch: waterPlanPatchFromOsmFeatures(point, result.features) });
      setOsmWaterStatus('done');
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident?.id, incident?.coordinates?.lng, incident?.coordinates?.lat]);

  // Deterministic candidates — recompute whenever the incident or roster changes.
  const candidates = useMemo<Insight[]>(
    () =>
      incident
        ? evaluate({ incident, config, nowMs: Date.now(), spatial: spatialInsightContext(incident), resolvedParams, roster: roster ?? undefined })
        : [],
    [incident, config, resolvedParams, roster],
  );

  // Audit the appearance / auto-resolution of cards as the context evolves — tracked
  // per incident id so switching between incidents doesn't spuriously log every one
  // of the other incident's insights as newly shown/auto-resolved.
  const prevIdsByIncidentRef = useRef<Record<string, Set<string>>>({});
  useEffect(() => {
    if (!incident) return;
    const id = incident.id;
    const prevIds = prevIdsByIncidentRef.current[id] ?? new Set<string>();
    const cur = new Set(candidates.map((c) => c.id));
    const events: AuditEvent[] = [];
    for (const c of candidates) {
      if (!prevIds.has(c.id)) {
        events.push(
          createAuditEvent({
            incidentId: id,
            type: 'insight.shown',
            actor: 'system',
            source: 'derived',
            payload: { insightId: c.id, title: c.title, severity: c.severity },
          }),
        );
      }
    }
    for (const pid of prevIds) {
      if (!cur.has(pid)) {
        events.push(
          createAuditEvent({ incidentId: id, type: 'insight.auto-resolved', actor: 'system', source: 'derived', payload: { insightId: pid } }),
        );
      }
    }
    if (events.length) {
      setStore((prev) => {
        const record = prev.recordsById[id];
        if (!record) return prev;
        return { ...prev, recordsById: { ...prev.recordsById, [id]: { ...record, audit: [...record.audit, ...events] } } };
      });
    }
    prevIdsByIncidentRef.current[id] = cur;
  }, [candidates, incident]);

  const insights = useMemo<Insight[]>(
    () =>
      candidates.map((c) => {
        const it = interactions[c.id];
        return it ? { ...c, status: it.status, dismissReason: it.dismissReason } : c;
      }),
    [candidates, interactions],
  );

  /** Merge a patch into the active record's interactions/audit — shared by acknowledge/dismiss. */
  const patchActiveRecord = useCallback(
    (build: (record: PersistedIncidentRecord) => Partial<PersistedIncidentRecord>) => {
      setStore((prev) => {
        if (!prev.activeId) return prev;
        const record = prev.recordsById[prev.activeId];
        if (!record) return prev;
        return { ...prev, recordsById: { ...prev.recordsById, [prev.activeId]: { ...record, ...build(record) } } };
      });
    },
    [],
  );

  const acknowledgeInsight = useCallback(
    (id: string) => {
      if (!incident) return;
      const event = createAuditEvent({ incidentId: incident.id, type: 'insight.acknowledged', actor: actorForRole(role), source: 'manual', payload: { insightId: id } });
      const nextInteraction: Interaction = { status: 'acknowledged' };
      patchActiveRecord((record) => ({ interactions: { ...record.interactions, [id]: nextInteraction }, audit: [...record.audit, event] }));
    },
    [incident, role, patchActiveRecord],
  );

  const dismissInsight = useCallback(
    (id: string, reason: string) => {
      if (!incident) return;
      const event = createAuditEvent({ incidentId: incident.id, type: 'insight.dismissed', actor: actorForRole(role), source: 'manual', payload: { insightId: id, reason } });
      const nextInteraction: Interaction = { status: 'dismissed', dismissReason: reason };
      patchActiveRecord((record) => ({ interactions: { ...record.interactions, [id]: nextInteraction }, audit: [...record.audit, event] }));
    },
    [incident, role, patchActiveRecord],
  );

  const logEvent = useCallback(
    (type: AuditEventType, payload?: Record<string, unknown>) => {
      if (!incident) return;
      const event = createAuditEvent({ incidentId: incident.id, type, actor: actorForRole(role), source: 'manual', payload });
      patchActiveRecord((record) => ({ audit: [...record.audit, event] }));
    },
    [incident, role, patchActiveRecord],
  );

  const loadRoster = (r: MemberRoster) => dispatchRoster({ type: 'load-roster', roster: r });
  const clearRoster = () => dispatchRoster({ type: 'clear-roster' });

  const value = useMemo(
    () => ({
      incident,
      dispatch,
      config,
      insights,
      audit,
      roster,
      loadRoster,
      clearRoster,
      acknowledgeInsight,
      dismissInsight,
      logEvent,
      osmWaterStatus,
      incidentList,
      switchToIncident,
      returnToDashboard,
      removeIncident,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [incident, config, insights, audit, roster, osmWaterStatus, incidentList, dispatch, acknowledgeInsight, dismissInsight, logEvent, switchToIncident, returnToDashboard, removeIncident],
  );
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useIncidentStore(): StoreValue {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useIncidentStore must be used within IncidentProvider');
  return ctx;
}
